/**
 * "Dealer talk": the same messages in three voices.
 *   vegas: a wisecracking Las Vegas dealer (the default). Spicy PG-13: teases the cards and the luck, never the person.
 *   afterDark: "Vegas After Dark", the same dealer after midnight. Adults only, opt-in: real swearing, crude jokes.
 *     Still teases the play, never who someone is. No slurs and nothing sexual (test/voice-lines.test.ts checks).
 *   plain: clear and simple
 *
 * Each player picks their own voice in the table Menu; it only changes wording, never rules or buttons.
 * All the Vegas and After Dark lines live here so they're easy to make milder or spicier.
 * After Dark is web-only: the iPhone/iPad app offers Plain and Vegas (David, Oct 2026, for the 13+ App Store rating).
 */

export type DealerTalk = 'plain' | 'vegas' | 'afterDark';

const KEY = 'euchre.dealerTalk';

export function storedDealerTalk(): DealerTalk {
  try {
    // Vegas unless this player picked Plain or After Dark.
    const v = localStorage.getItem(KEY);
    return v === 'plain' || v === 'afterDark' ? v : 'vegas';
  } catch {
    return 'vegas';
  }
}

/** A heckle's text in this player's voice: the After Dark version when they picked After Dark (and the server sent one). */
export function heckleText(e: { text: string; afterDark?: string }, talk: DealerTalk): string {
  return talk === 'afterDark' && e.afterDark ? e.afterDark : e.text;
}

/** The lines for a dealer voice (Plain has none: callers use their plain wording). */
export function voiceLines(talk: DealerTalk): DealerLines | null {
  return talk === 'vegas' ? vegas : talk === 'afterDark' ? afterDark : null;
}

export function storeDealerTalk(v: DealerTalk): void {
  try {
    localStorage.setItem(KEY, v);
  } catch {
    /* private mode: lasts for this tab */
  }
}

/** Pick a line that stays the same while the game state is the same (so it doesn't flicker on every redraw). */
export function pick(lines: readonly string[], seed: string): string {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
  return lines[(h >>> 0) % lines.length]!;
}

export const vegas = {
  turnBadge: [
    'You’re up', 'Showtime', 'Your move, ace', 'Action’s on you', 'All eyes on you', 'Clock’s ticking', 'Big moment, kid',
    'Don’t be shy', 'Your turn, champ', 'Make it count', 'No pressure',
  ],

  lead: (verb: string) => [
    `Your lead. Make it count. Or don’t, I get paid either way. ${verb} any card.`,
    `The floor is yours, hotshot. ${verb} any card and try to look confident.`,
    `You’re leading. No pressure, the whole table is just staring at you. ${verb} any card.`,
    `Lead off, sport. ${verb} any card; the crowd’s getting restless.`,
    `Your lead. Pick a card like you’ve done this before. ${verb} any card.`,
    `Your lead, big shooter. Show us what you’re working with. ${verb} any card.`,
    `You’re on top this time. Set the pace. ${verb} any card.`,
    `Lead us somewhere nice, partner. ${verb} any card.`,
  ],
  play: (verb: string) => [
    `${verb} a card. Any time this century.`,
    `Go on, ${verb.toLowerCase()} a card. The table’s not getting any younger.`,
    `${verb} a card, high roller. Dazzle us.`,
    `${verb} a card. Today, preferably.`,
    `${verb} a card and pretend you planned this all along.`,
    `Go ahead, lay one down. The suspense is killing us.`,
    `${verb} a card, friend. Nobody’s getting any younger and the drinks are watered down.`,
    `${verb} a card. Confidence is good. Speed is better.`,
  ],
  follow: (suit: string, verb: string) => [
    `${suit} were led, chief. ${verb} a bright card; the grey ones are just for decoration.`,
    `Follow ${suit}, pal. The grey cards are off the menu tonight.`,
    `It’s ${suit} or nothing, champ. Bright cards only, house rules.`,
    `Rules are rules, buttercup: ${suit} only. The grey ones are benched.`,
    `${suit} were led. ${verb} a bright card; the grey ones are just here for moral support.`,
    `${suit} were led, ace. Stick with the suit; the grey ones aren’t invited.`,
    `Follow ${suit}. I don’t make the rules. I just enforce ’em with a smile.`,
  ],
  discard: (card: string, verb: string) => [
    `You grabbed the ${card}. Now ${verb.toLowerCase()} something to toss. Nobody likes a hoarder.`,
    `Nice pickup. ${verb} a card to throw back and act like you meant it.`,
    `${verb} a card to bury. We won’t tell anybody.`,
    `One of these has to go, champ. ${verb} a card to discard.`,
    `One card has to go. Break it to ’em gently. ${verb} it.`,
  ],
  orderUp: (card: string, suit: string, dealer: boolean) =>
    dealer
      ? [
          `Pick up the ${card} and make ${suit} trump? Your call, big shot.`,
          `The ${card} is right there, dealer. Scoop it up and make ${suit} trump?`,
          `Your deal, your ${card}. Take it and make ${suit} trump?`,
          `That ${card} is giving you eyes, dealer. Take it home and make ${suit} trump?`,
        ]
      : [
          `Make the dealer pick up the ${card} and call ${suit} trump? Feeling lucky?`,
          `Order up the ${card} and make ${suit} trump? Go big or go home.`,
          `Tell the dealer to grab the ${card} and make ${suit} trump? Bold. I like it.`,
          `Send the ${card} to the dealer and make ${suit} trump? Buy it a drink first.`,
        ],
  nameTrump: (out: string) => [
    `Name trump (anything but ${out}) or pass. Choose wisely… or don’t.`,
    `${out} are dead to us. Pick a new trump or pass, and make it snappy.`,
    `${out} got turned down harder than a prom date. Name a new trump or pass.`,
    `${out} got turned down. Name a new trump or pass; no take-backs.`,
    `${out} are out. Plenty of other fish. Name one or pass.`,
  ],
  stuck: [
    'Stick the dealer, pal. You’re naming trump whether you like it or not.',
    'Nobody wanted it, so it’s all yours, dealer. Name trump.',
    'Everybody chickened out. Congratulations, dealer, you’re naming trump.',
    'Stuck, dealer. No more playing hard to get. Name trump.',
  ],
  defendAlone: (maker: string, suit: string, alone: boolean, points: number) => [
    `${maker} called ${suit}${alone ? ' and is going solo' : ''}. Brave enough to defend alone? Your partner sits this one out, and stopping them alone is ${points} points of pure bragging rights.`,
    `${maker} called ${suit}${alone ? ' and wants to go solo' : ''}. Want to go it alone and stop them? Your partner grabs a snack, and a lone euchre is worth ${points} points.`,
  ],

  waiting: {
    deal: [
      'Checking the deal for funny business. Nobody touch anything.',
      'Counting the cards. Twice. This is a classy joint.',
      'Shuffling. Keep your hands where I can see ’em.',
    ],
    bid: (who: string) => [
      `${who} is deciding. Bless their heart…`,
      `${who} is thinking about trump. Don’t rush genius…`,
      `${who} is staring at their cards like they owe them money…`,
      `${who} is doing the math. Give ’em a minute…`,
      `${who} is weighing their options. All two of them…`,
      `${who} is playing hard to get…`,
      `${who} is thinking it over. We’ll wait. We always wait…`,
    ],
    defend: (who: string) => [`${who} is deciding whether to go it alone. Brave or foolish, we’ll see…`, `${who} is feeling brave. Or reckless. Stand by…`],
    discard: (who: string, card: string) => [
      `${who} grabbed the ${card} and is pretending to know what to throw away…`,
      `${who} is deciding which card gets voted off the island…`,
      `${who} is picking which card to dump. Breakups are hard…`,
    ],
    bot: (who: string) => [
      `${who}’s crunching numbers. It’s a bot; it lives for this…`,
      `${who} is consulting its circuits…`,
      `${who} is thinking. Well, “thinking.” It’s a bot…`,
      `${who} is running the odds. Bots never bluff… probably…`,
      `${who} is buffering…`,
      `${who} is warming up its processor. Things are getting steamy in there…`,
      `${who} is a bot. It doesn’t sweat, but this one’s close…`,
    ],
    person: (who: string) => [
      `${who} is thinking. Or napping. Hard to tell…`,
      `Waiting on ${who}. Somebody nudge them…`,
      `${who} is up. Hum something while you wait…`,
      `${who} is taking their sweet time…`,
      `Somebody tell ${who} the meter’s running…`,
      `${who} is up. Probably texting their ex…`,
      `${who} is thinking so hard you can hear it…`,
    ],
  },

  takesIt: (who: string, you: boolean) =>
    you
      ? [`You rake it in`, `Yours. Cha-ching`, `All yours, champ`, `You take it home`, `Nice. Yours`]
      : [`${who} rakes it in`, `${who} takes it. Cha-ching`, `${who} scoops it up`, `${who} wins that one. Rude`, `${who} takes it home`],

  result: {
    euchreUs: [
      'Euchred! The house thanks you for your donation.', 'Euchred. Well, damn. That’ll leave a mark.', 'Euchred. Let’s never speak of this again.',
      'Euchred. That’ll leave a mark, and not the fun kind.',
    ],
    euchreThem: [
      'Euchred them! Somebody call a medic.', 'You euchred ’em! Pour one out for the other team.', 'Euchred ’em! That’s gonna sting in the morning.',
      'Euchred ’em! Go ahead, do a little dance.',
    ],
    marchUs: ['A march! Somebody call security.', 'All five tricks. Show-off.', 'All five! Somebody check your sleeves.', 'All five! Somebody get these folks a cigarette.'],
    marchThem: ['They took all five. Yikes.', 'A march for them. Brutal.', 'Swept. Clean sweep. Ouch.', 'All five for them. You got worked over.'],
    madeUs: ['Hand made. Not pretty, but it counts.', 'Made it. Barely legal, but legal.', 'Got it done. Ugly wins still count.', 'Made it. Nobody’s complaining.'],
    madeThem: ['They made it. Barely.', 'They got their points. Don’t let it go to their heads.', 'They squeaked by. Sheer dumb luck, obviously.', 'They got it done. Nobody’s impressed.'],
  },
  stillThere: ['Hey! You still with us?', 'Yoo-hoo! Anybody home?', 'Earth to you!', 'Did you fall in?'],
  dealing: ['Shuffling up and dealing…', 'Fresh cards coming. Try to look surprised…', 'New hand, new excuses…', 'Fresh deck. Fresh start. Same old you…'],
  win: [
    'Winner winner, chicken dinner!', 'You win! Drinks are on… well, not the house.', 'You win! Act humble. Or don’t.',
    'You win! Somebody’s getting lucky tonight.',
  ],
  lose: [
    'The house always wins. Tonight the house was them.', 'They got you. Hey, there’s always the next game.', 'Tough loss. The cards were rigged. Allegedly.',
    'You lost. Happens to the best of us. Mostly to you, tonight.',
  ],
};

/** Every dealer voice has the same moments as Vegas. */
export type DealerLines = typeof vegas;

/**
 * Vegas After Dark: same dealer, later shift, fewer filters. Adults only, opt-in.
 * Rules for these lines: swearing is fine; slurs, sexual content and jokes about who someone is are not.
 * Roast the play and the luck, not the person. Don't change a player's name or the card and suit words.
 */
export const afterDark: DealerLines = {
  turnBadge: [
    'Your fucking turn', 'Move it, sunshine', 'You’re up, dumbass', 'Showtime, shithead', 'Do something',
    'Clock’s ticking, asshole', 'Your turn, hotshot', 'Wake the hell up', 'Don’t screw this up', 'Go, damn it', 'Big moment. Don’t blow it',
  ],

  lead: (verb: string) => [
    `Your lead, hotshot. Don’t overthink it, you’ll strain something. ${verb} any card.`,
    `You’re leading. God help us all. ${verb} any card.`,
    `Lead something. Anything. Christ, we’re aging over here. ${verb} any card.`,
    `Your lead. Pick a card like you’ve got a clue what the hell you’re doing. ${verb} any card.`,
    `The floor is yours, genius. Try not to shit the bed. ${verb} any card.`,
    `Lead off, big shot. The whole damn table is watching. ${verb} any card.`,
    `Your lead. Make it count, or at least make it quick. ${verb} any card.`,
    `You’re on lead. No pressure, but everybody’s judging you. ${verb} any card.`,
  ],
  play: (verb: string) => [
    `Holy shit, play a card already. The drinks are melting. ${verb} one.`,
    `${verb} a card. Any card. Jesus, it’s not brain surgery.`,
    `${verb} a card before I die of old age, dammit.`,
    `${verb} a card, hotshot. We haven’t got all goddamn night.`,
    `${verb} a card and act like you meant it. Nobody’ll know the difference.`,
    `${verb} a card. Hell, close your eyes and pick one. It can’t go worse.`,
    `${verb} a card. The meter’s running and the pit boss is pissed.`,
    `${verb} a card, hotshot. This isn’t a fucking museum.`,
  ],
  follow: (suit: string, verb: string) => [
    `${suit} were led, genius. ${verb} a bright card; the grey ones are just for show, like your poker face.`,
    `Follow ${suit}, dumbass. The grey cards are off the damn menu.`,
    `It’s ${suit} or nothing, pal. ${verb} a bright card and quit screwing around.`,
    `${suit} were led. ${verb} a bright card. I don’t make the rules, I just enforce the hell out of ’em.`,
    `${suit}, champ. Bright cards only. The grey ones are as useless as your last bid.`,
    `${suit} were led. ${verb} a bright card; the grey ones can go piss up a rope.`,
    `Follow ${suit}. Rules are rules, even for a hotshot like you. ${verb} a bright card.`,
  ],
  discard: (card: string, verb: string) => [
    `You grabbed the ${card}. Now ${verb} some crap to throw away.`,
    `Nice pickup, hotshot. ${verb} your worst piece of shit and bury it.`,
    `Something’s gotta go. ${verb} the card you’re most ashamed of.`,
    `${verb} a card to ditch. We won’t tell anybody how bad it was.`,
    `Dump one, dealer. ${verb} the junk and pretend it was strategy.`,
  ],
  orderUp: (card: string, suit: string, dealer: boolean) =>
    dealer
      ? [
          `Pick up the ${card} and make ${suit} trump? Shit or get off the pot, dealer.`,
          `The ${card} is sitting right there, dealer. Grab it and make ${suit} trump, or don’t, whatever.`,
          `Your deal, your damn ${card}. Take it and make ${suit} trump?`,
          `That ${card} is begging, dealer. Make ${suit} trump or quit wasting everybody’s time.`,
        ]
      : [
          `Order up the ${card} and make ${suit} trump? Put up or shut up.`,
          `Make the dealer take the ${card} and call ${suit} trump? Gutsy as hell. I like it.`,
          `Send the dealer the ${card} and make ${suit} trump? Go big or go the hell home.`,
          `Order up the ${card} and make ${suit} trump? Feeling lucky, or just stupid?`,
        ],
  nameTrump: (out: string) => [
    `${out} are dead to us. Pick a new trump or pass, and don’t fuck it up.`,
    `${out} got dumped. Name a new trump or pass, and make it snappy, dammit.`,
    `Nobody wanted ${out}. Name something else or pass. Christ, it’s not a marriage proposal.`,
    `${out} are out. Name a new trump or pass. Don’t just sit there like a damn lump.`,
    `${out} are history. Pick a trump or pass, and spare us the drama.`,
  ],
  stuck: [
    'Stuck, dealer. Nobody else had the guts. Name trump.',
    'Stick the dealer, pal. Name trump and quit your whining.',
    'Everybody chickened out, so it’s your ass on the line. Name trump.',
    'Stuck with it, dealer. Suck it up and name trump.',
  ],
  defendAlone: (maker: string, suit: string, alone: boolean, points: number) => [
    `${maker} called ${suit}${alone ? ' and is going solo, the arrogant bastard' : ''}. Got the guts to defend alone? Your partner sits this one out, and stopping them alone is worth ${points} points of pure bragging rights.`,
    `${maker} called ${suit}${alone ? ' and wants to go it alone' : ''}. Want to stop them all by your damn self? Your partner grabs a beer, and a lone euchre is worth ${points} points.`,
  ],

  waiting: {
    deal: [
      'Checking the deal for bullshit. Nobody touch a damn thing.',
      'Counting the cards. Twice. Some of you assholes can’t be trusted.',
      'Shuffling. Keep your grubby hands where I can see ’em.',
    ],
    bid: (who: string) => [
      `${who} is deciding. Christ, it’s trump, not a mortgage…`,
      `${who} is staring at their cards like they owe them money…`,
      `${who} is thinking about trump. Don’t hurt yourself…`,
      `${who} is doing the math. Using their fingers, by the look of it…`,
      `${who} is weighing their options. All two of the damn things…`,
      `${who} is stalling. Somebody light a fire under their ass…`,
      `${who} is thinking it over. We’ll wait. We always fucking wait…`,
    ],
    defend: (who: string) => [`${who} is deciding whether to go it alone. Gutsy or brainless, we’ll see…`, `${who} is feeling brave. Or drunk. Stand by…`],
    discard: (who: string, card: string) => [
      `${who} grabbed the ${card} and is deciding which piece of crap to throw away…`,
      `${who} is picking which card gets kicked to the curb…`,
      `${who} is dumping a card. Breakups are hell…`,
    ],
    bot: (who: string) => [
      `${who} is crunching numbers. At least the bot knows what the hell it’s doing…`,
      `${who} is consulting its circuits. Smarter than some people at this table…`,
      `${who} is thinking. Well, “thinking.” It’s a damn bot…`,
      `${who} is running the odds. Bots don’t bluff, unlike you lying bastards…`,
      `${who} is buffering. Hurry up, you overgrown calculator…`,
      `${who} is judging your last play. Even the robot thinks it was shit…`,
      `${who} is a bot. It doesn’t sweat, but it’s sure as hell judging…`,
    ],
    person: (who: string) => [
      `Waiting on ${who}. Either they’re thinking or they went to take a leak…`,
      `${who} is thinking. Or napping. Somebody poke the son of a gun…`,
      `${who} is taking their sweet-ass time…`,
      `Somebody tell ${who} the meter’s running, dammit…`,
      `${who} is up. Probably drunk-texting their ex…`,
      `${who} is thinking so hard you can smell it…`,
      `Waiting on ${who}. Grab a drink, this could take a while…`,
    ],
  },

  takesIt: (who: string, you: boolean) =>
    you
      ? [`You rake it in`, `Yours. Hell yeah`, `All yours, badass`, `You take it, you lucky bastard`, `Damn right. Yours`]
      : [`${who} rakes it in`, `${who} takes it. Bastard`, `${who} scoops it up. Ugh`, `${who} wins that one. Rude as hell`, `${who} takes it home`],

  result: {
    euchreUs: [
      'Euchred. That was a hot steaming pile of shit.', 'Euchred. Well, fuck. That’ll leave a mark.', 'Euchred. Let’s never speak of this shitshow again.',
      'Euchred. The house thanks you for your dumbass donation.',
    ],
    euchreThem: [
      'You euchred ’em! Their asses are still smoking.', 'Euchred ’em! Somebody get those poor bastards a drink.', 'Euchred ’em! That’s gonna hurt like hell in the morning.',
      'Euchred ’em! Go ahead, rub it in. They earned it.',
    ],
    marchUs: ['All five! Absolute badass move.', 'A march! Holy shit, somebody call security.', 'All five tricks, you show-off son of a gun.', 'All five! Check their sleeves, that’s some cheating-ass luck.'],
    marchThem: ['They took all five. You got your ass handed to you.', 'A march for them. Brutal as hell.', 'Swept clean. Well, shit.', 'All five for them. That was a goddamn massacre.'],
    madeUs: ['Hand made. Ugly as hell, but it counts.', 'Made it. Barely, but screw it, points are points.', 'Got it done. Not pretty, but who gives a damn.', 'Made it. Nobody’s complaining, least of all me.'],
    madeThem: ['They made it. Lucky bastards.', 'They got their points. Don’t let it go to their damn heads.', 'They squeaked by. Pure dumb-ass luck.', 'They got it done. Nobody’s impressed, assholes.'],
  },
  stillThere: ['Hey! You still with us, or what?', 'Yoo-hoo! Anybody home, dammit?', 'Earth to you! Wake the hell up!', 'Did you fall in the damn toilet?'],
  dealing: ['Shuffling up and dealing, God help us…', 'Fresh cards coming. Try not to screw these up…', 'New hand, same damn excuses…', 'Fresh deck. Fresh start. Same old dumbass plays…'],
  win: [
    'You win! Go ahead, be an insufferable asshole about it. You’ve earned it.', 'Winner winner, chicken dinner, suckers!', 'You win! Drinks are on you, hotshot.',
    'You win! Holy shit, you actually pulled it off.',
  ],
  lose: [
    'You lost. That was a fucking clinic… for the other team.', 'They kicked your ass. There’s always the next game.', 'Tough loss. The cards were rigged. Allegedly. Probably not.',
    'You lost. Shit happens. Mostly to you, tonight.',
  ],
};
